import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import { alpha } from '@mui/material/styles';
import AuthService from '../services/auth';
import apiClient, { getErrorMessage, shouldRedirectToLogin } from '../services/api-client';
import CustomSnackbar from '../components/CustomSnackbar';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Administrador' },
  { value: 'habitante', label: 'Residente' },
  { value: 'receptor', label: 'Recepción' },
];

const ROLE_LABEL_MAP = ROLE_OPTIONS.reduce((acc, role) => ({ ...acc, [role.value]: role.label }), {});
const ALL_ROLE_VALUES = ROLE_OPTIONS.map((role) => role.value);

function getRoleLabels(values) {
  if (!Array.isArray(values) || values.length === 0) {
    return 'todos';
  }
  return values.map((value) => ROLE_LABEL_MAP[value] || value).join(', ');
}

const EMPTY_FORM = {
  title: '',
  main_message: '',
  sub_message: '',
  target_roles: ['admin', 'habitante', 'receptor'],
  target_unit_patterns: [],
  expires_at_input: '',
};

function formatDateTimeInput(ts) {
  if (!ts) {
    return '';
  }
  const date = new Date(Number(ts) * 1000);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const offsetMs = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

function toUnixTimestamp(value) {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return Math.round(date.getTime() / 1000);
}

function formatDisplayDate(ts) {
  if (!ts) {
    return '—';
  }
  try {
    return new Date(Number(ts) * 1000).toLocaleString('es-GT', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '—';
  }
}

export default function AdminBannerNotifications({ setNavbarState, navbarState }) {
  const navigate = useNavigate();
  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [editingId, setEditingId] = React.useState('');
  const [error, setError] = React.useState('');
  const [totalUsers, setTotalUsers] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const fetchNotifications = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('admin/banner_notifications', {}, { action: 'fetch_admin_banner_notifications' });
      setNotifications(response?.data?.data?.notifications || []);
      setError('');
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setError(getErrorMessage(fetchError, 'No pude cargar las notificaciones.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalUsers = React.useCallback(async () => {
    try {
      const response = await apiClient.get('users/allusersadmin', {}, { action: 'fetch_all_users_admin_for_banner_notifications' });
      const users = response?.data?.data;
      if (Array.isArray(users)) {
        setTotalUsers(users.length);
      }
    } catch (fetchError) {
      if (shouldRedirectToLogin(fetchError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setTotalUsers(null);
    }
  }, []);

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: true, showOptions: false, showSearch: false });
    fetchNotifications();
    fetchTotalUsers();
  }, []);

  const resetForm = React.useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId('');
  }, []);

  const handleChange = React.useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleRoleToggle = React.useCallback((roleValue) => {
    setForm((prev) => {
      const current = Array.isArray(prev.target_roles) ? prev.target_roles : [];
      const exists = current.includes(roleValue);
      return {
        ...prev,
        target_roles: exists ? current.filter((item) => item !== roleValue) : [...current, roleValue],
      };
    });
  }, []);

  const handleUnitPatternsChange = React.useCallback((value) => {
    handleChange(
      'target_unit_patterns',
      String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    );
  }, [handleChange]);

  const isTargetingAllUsers = React.useCallback((notification) => {
    const targetRoles = Array.isArray(notification?.target_roles) ? notification.target_roles : [];
    const targetPatterns = Array.isArray(notification?.target_unit_patterns) ? notification.target_unit_patterns : [];
    const normalizedRoles = targetRoles.map((role) => String(role || '').toLowerCase()).filter(Boolean);
    const hasAllRoles = ALL_ROLE_VALUES.every((role) => normalizedRoles.includes(role)) || normalizedRoles.length === 0;
    return hasAllRoles && targetPatterns.length === 0;
  }, []);

  const getTargetedCount = React.useCallback((notification) => {
    if (isTargetingAllUsers(notification) && Number.isFinite(totalUsers)) {
      return totalUsers;
    }
    return notification?.stats?.targeted || 0;
  }, [isTargetingAllUsers, totalUsers]);

  const submitPayload = React.useMemo(() => {
    const expiresAt = toUnixTimestamp(form.expires_at_input);
    return {
      title: String(form.title || '').trim(),
      main_message: String(form.main_message || '').trim(),
      sub_message: String(form.sub_message || '').trim(),
      target_roles: Array.isArray(form.target_roles) ? form.target_roles : [],
      target_unit_patterns: Array.isArray(form.target_unit_patterns) ? form.target_unit_patterns : [],
      ...(expiresAt ? { expires_at: expiresAt } : {}),
    };
  }, [form]);

  const handleSubmit = React.useCallback(async () => {
    if (!submitPayload.title || !submitPayload.main_message) {
      setSnackbar({ open: true, message: 'Título y mensaje principal son obligatorios.', severity: 'warning' });
      return;
    }
    if (!submitPayload.target_roles.length) {
      setSnackbar({ open: true, message: 'Selecciona al menos un rol objetivo.', severity: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (editingId) {
        await apiClient.put(`admin/banner_notifications/${editingId}`, submitPayload, {}, { action: 'update_admin_banner_notification' });
        setSnackbar({ open: true, message: 'Notificación actualizada.', severity: 'success' });
      } else {
        await apiClient.post('admin/banner_notifications', submitPayload, {}, { action: 'create_admin_banner_notification' });
        setSnackbar({ open: true, message: 'Notificación creada.', severity: 'success' });
      }
      resetForm();
      fetchNotifications();
    } catch (saveError) {
      if (shouldRedirectToLogin(saveError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(saveError, 'No pude guardar la notificación.'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [editingId, fetchNotifications, resetForm, submitPayload]);

  const handleEdit = React.useCallback((notification) => {
    setEditingId(notification?.id || '');
    setForm({
      title: notification?.title || '',
      main_message: notification?.main_message || '',
      sub_message: notification?.sub_message || '',
      target_roles: Array.isArray(notification?.target_roles) ? notification.target_roles : [],
      target_unit_patterns: Array.isArray(notification?.target_unit_patterns) ? notification.target_unit_patterns : [],
      expires_at_input: formatDateTimeInput(notification?.expires_at),
    });
  }, []);

  const handleCancelNotification = React.useCallback(async (notificationId) => {
    try {
      await apiClient.post(`admin/banner_notifications/${notificationId}/cancel`, {}, {}, { action: 'cancel_admin_banner_notification' });
      setSnackbar({ open: true, message: 'Notificación cancelada.', severity: 'success' });
      fetchNotifications();
      if (editingId === notificationId) {
        resetForm();
      }
    } catch (cancelError) {
      if (shouldRedirectToLogin(cancelError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(cancelError, 'No pude cancelar la notificación.'), severity: 'error' });
    }
  }, [editingId, fetchNotifications, resetForm]);

  const handleDeleteNotification = React.useCallback(async (notificationId) => {
    if (!notificationId) {
      return;
    }
    const confirmed = window.confirm('Eliminar el anuncio de forma permanente? Esta acción no se puede deshacer.');
    if (!confirmed) {
      return;
    }
    try {
      await apiClient.delete(`admin/banner_notifications/${notificationId}`, {}, { action: 'delete_admin_banner_notification' });
      setSnackbar({ open: true, message: 'Notificación eliminada.', severity: 'success' });
      fetchNotifications();
      if (editingId === notificationId) {
        resetForm();
      }
    } catch (deleteError) {
      if (shouldRedirectToLogin(deleteError)) {
        setRedirect({ trigger: true, route: '/login' });
        return;
      }
      setSnackbar({ open: true, message: getErrorMessage(deleteError, 'No pude eliminar la notificación.'), severity: 'error' });
    }
  }, [editingId, fetchNotifications, resetForm]);

  if (!AuthService.userRoleIncludes(['admin'])) {
    return <Navigate to="/" replace={true} />;
  }

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" disableGutters>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 12 }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            px: { xs: 2, sm: 3 },
            pt: 2.5,
            pb: 2,
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  width: 40,
                  height: 40,
                  ml: -1,
                  color: 'text.primary',
                  '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <Box>
                <Typography sx={{ fontSize: 24, fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
                  Anunciar
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                  Crea, edita y cancela anuncios para administradores, residentes y recepción.
                </Typography>
              </Box>
            </Stack>
            <Button variant="text" onClick={fetchNotifications} disabled={loading}>
              Refrescar
            </Button>
          </Stack>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
          <Stack spacing={3}>
            {error && <Alert severity="error">{error}</Alert>}

            <Card sx={{ borderRadius: 3, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                <Stack spacing={2.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>
                        {editingId ? 'Editar notificación' : 'Nueva notificación'}
                      </Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
                        Expiración por defecto: 1 mes si no defines una fecha.
                      </Typography>
                    </Box>
                    {editingId && (
                      <Button variant="outlined" onClick={resetForm}>
                        Cancelar edición
                      </Button>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <TextField label="Título" value={form.title} onChange={(event) => handleChange('title', event.target.value)} fullWidth />
                    </Box>
                    <Box>
                      <TextField
                        label="Expira en"
                        type="datetime-local"
                        value={form.expires_at_input}
                        onChange={(event) => handleChange('expires_at_input', event.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Mensaje principal"
                        value={form.main_message}
                        onChange={(event) => handleChange('main_message', event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Mensaje secundario"
                        value={form.sub_message}
                        onChange={(event) => handleChange('sub_message', event.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    </Box>
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <TextField
                        label="Patrones de unidad"
                        value={(form.target_unit_patterns || []).join(', ')}
                        onChange={(event) => handleUnitPatternsChange(event.target.value)}
                        helperText="Opcional. Usa comas, por ejemplo: t1*, a-2"
                        fullWidth
                      />
                    </Box>
                  </Box>

                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.secondary', mb: 1 }}>
                      Roles objetivo
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {ROLE_OPTIONS.map((role) => {
                        const selected = (form.target_roles || []).includes(role.value);
                        return (
                          <Chip
                            key={role.value}
                            label={role.label}
                            color={selected ? 'primary' : 'default'}
                            variant={selected ? 'filled' : 'outlined'}
                            onClick={() => handleRoleToggle(role.value)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button variant="outlined" onClick={resetForm} disabled={saving}>
                      Limpiar
                    </Button>
                    <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                      {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Crear notificación'}
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ px: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: 'text.primary' }}>
                      Anuncios activos e históricos
                    </Typography>
                  </Box>

                  {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : notifications.length === 0 ? (
                    <Alert severity="info">Aún no hay anuncios.</Alert>
                  ) : (
                    <Stack spacing={2}>
                      {notifications.map((notification) => {
                        const statCards = [
                          { label: 'Objetivo', value: getTargetedCount(notification) },
                          { label: 'Entregadas', value: notification.stats?.delivered || 0 },
                          { label: 'Vistas', value: notification.stats?.viewed || 0 },
                          { label: 'Descartadas', value: notification.stats?.dismissed || 0 },
                          { label: 'Pendientes', value: notification.stats?.pending || 0 },
                        ];

                        return (
                          <Card key={notification.id} sx={{ borderRadius: 3, boxShadow: 0, border: '1px solid', borderColor: 'divider' }}>
                            <CardContent>
                              <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap', useFlexGap: true }}>
                                      <CampaignOutlinedIcon sx={{ color: 'primary.main' }} />
                                      <Typography sx={{ fontSize: 17, fontWeight: 800, color: 'text.primary' }}>
                                        {notification.title || 'Sin título'}
                                      </Typography>
                                      <Chip label={notification.status || 'active'} size="small" color={notification.status === 'cancelled' ? 'default' : 'primary'} />
                                    </Stack>
                                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.secondary' }}>
                                      {notification.main_message || notification.sub_message || 'Sin contenido'}
                                    </Typography>
                                  </Box>
                                  <Stack direction="row" spacing={1}>
                                    <IconButton onClick={() => handleEdit(notification)}>
                                      <EditOutlinedIcon />
                                    </IconButton>
                                    {notification.status !== 'cancelled' && (
                                      <IconButton color="error" onClick={() => handleCancelNotification(notification.id)}>
                                        <CancelOutlinedIcon />
                                      </IconButton>
                                    )}
                                    {notification.status === 'cancelled' && (
                                      <IconButton color="error" onClick={() => handleDeleteNotification(notification.id)}>
                                        <DeleteForeverOutlinedIcon />
                                      </IconButton>
                                    )}
                                  </Stack>
                                </Box>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  <Chip label={`Roles: ${getRoleLabels(notification.target_roles)}`} size="small" variant="outlined" />
                                  <Chip label={`Unidades: ${(notification.target_unit_patterns || []).join(', ') || 'todas'}`} size="small" variant="outlined" />
                                  <Chip label={`Expira: ${formatDisplayDate(notification.expires_at)}`} size="small" variant="outlined" />
                                </Stack>

                                <Divider />

                                <Box
                                  sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(5, minmax(0, 1fr))' },
                                    gap: 1.5,
                                  }}
                                >
                                  {statCards.map((stat) => (
                                    <Box
                                      key={stat.label}
                                      sx={{
                                        p: 1.5,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        bgcolor: 'background.default',
                                      }}
                                    >
                                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }}>{stat.label}</Typography>
                                      <Typography sx={{ fontSize: 18, fontWeight: 900, color: 'text.primary' }}>{stat.value}</Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Box>

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        />
      </Box>
    </Container>
  );
}
